import { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import type {
  GenerateLicenseRequest,
  ILicenseRequest,
  LicenseRequestStatus,
  VerifyLicenseRequest,
} from "@/features/licenses/schemas/license";
import {
  deleteLicense,
  getLicensesByUser,
} from "@/features/licenses/services/licenseFirestore";
import { subscribeLicenseRequests } from "@/features/licenses/services/licenseRequestsFirestore";
import {
  approveLicenseRequest,
  generateLicense,
  rejectLicenseRequest,
  verifyLicense,
} from "@/features/licenses/services/licenses";

export const licenseKeys = {
  all: ["licenses"] as const,
  byUser: (userId: string) => [...licenseKeys.all, "user", userId] as const,
};

export const licenseRequestKeys = {
  all: ["licenseRequests"] as const,
  pending: () => [...licenseRequestKeys.all, "pending"] as const,
};

export function useLicensesByUser(userId: string) {
  return useQuery({
    queryKey: licenseKeys.byUser(userId),
    queryFn: () => getLicensesByUser(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGenerateLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GenerateLicenseRequest) => generateLicense(data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: licenseKeys.byUser(variables.userId),
      });
    },
  });
}

export function useVerifyLicense() {
  return useMutation({
    mutationFn: (data: VerifyLicenseRequest) => verifyLicense(data),
  });
}

export function useDeleteLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteLicense,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: licenseKeys.all });
    },
  });
}

type UseLicenseRequestsOptions = {
  status?: LicenseRequestStatus | "all";
  userId?: string;
  enabled?: boolean;
};

/**
 * Suscripción realtime a license_requests (cola de aprobación).
 */
export function useLicenseRequests(options: UseLicenseRequestsOptions = {}) {
  const { status = "pending", userId, enabled = true } = options;
  const [data, setData] = useState<ILicenseRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const unsubscribe = subscribeLicenseRequests(
      {
        status: status === "all" ? undefined : status,
        userId,
      },
      (rows) => {
        setData(rows);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      }
    );

    return unsubscribe;
  }, [status, userId, enabled]);

  return { data, isLoading, error, count: data.length };
}

/** Solo pendientes globales (campana / badge sidebar). */
export function usePendingLicenseRequests(enabled = true) {
  return useLicenseRequests({ status: "pending", enabled });
}

export function useApproveLicenseRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) => approveLicenseRequest(requestId),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: licenseKeys.all });
      void queryClient.invalidateQueries({ queryKey: licenseRequestKeys.all });
      toast.success(result.message || "Solicitud aprobada");
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : "No se pudo aprobar la solicitud"
      );
    },
  });
}

export function useRejectLicenseRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      requestId,
      reason,
    }: {
      requestId: string;
      reason?: string;
    }) => rejectLicenseRequest(requestId, reason),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: licenseRequestKeys.all });
      toast.success(result.message || "Solicitud rechazada");
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : "No se pudo rechazar la solicitud"
      );
    },
  });
}
